import React, { useState } from 'react';
import { Bell, Search, Menu, Settings, Lock, CheckCircle, AlertCircle, ChevronRight, User } from 'lucide-react';

const SurgeryCaseDashboard = () => {
  const [activeStage, setActiveStage] = useState('Doctor Pre-Lock');

  const stages = [
    { name: 'Lead Created', status: 'completed' },
    { name: 'Pre-Screening', status: 'completed' },
    { name: 'Admission', status: 'completed' },
    { name: 'Documents', status: 'completed' },
    { name: 'Clinical Decision', status: 'completed' },
    { name: 'Doctor Availability', status: 'current-yellow' },
    { name: 'Doctor Pre-Lock', status: 'current-green' },
    { name: 'Commercials', status: 'current-orange' },
    { name: 'Scheduling', status: 'pending' },
    { name: 'Payment', status: 'pending' },
    { name: 'OTP Start', status: 'pending' },
    { name: 'Post-Op', status: 'pending' },
  ];

  const getStageColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-[#3b5998] text-white';
      case 'current-yellow': return 'bg-[#8A9A3B] text-white';
      case 'current-green': return 'bg-[#6CAE54] text-white';
      case 'current-orange': return 'bg-[#E59845] text-white';
      default: return 'bg-[#8F9AA8] text-white opacity-80';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 font-sans">

      {/* Blue Sub-header */}
      <div className="bg-[#335380] text-white px-6 py-2.5 flex items-center justify-between text-sm shadow-inner">
        <div className="flex flex-wrap items-center gap-4 sm:gap-8 font-medium tracking-wide">
          <span>Case ID: <span className="font-bold">12543</span></span>
          <span className="hidden sm:inline">|</span>
          <span>Patient Name: <span className="font-bold">Ramesh Kumar</span></span>
          <span className="hidden sm:inline">|</span>
          <span>Stage: <span className="font-bold">Doctor Pre-Lock</span></span>
        </div>
        <Settings className="w-5 h-5 cursor-pointer hover:text-gray-300 transition-colors flex-shrink-0" />
      </div>

      {/* PCC Assignment Bar */}
      <div className="bg-white px-6 py-3 border-b border-gray-200 flex items-center shadow-sm">
        <div className="flex items-center border rounded border-gray-300 overflow-hidden shadow-sm">
          <div className="px-3 py-1.5 bg-gray-50 text-gray-700 text-sm font-medium border-r flex items-center gap-2 cursor-pointer hover:bg-gray-100">
            PCC Assigned <ChevronRight className="w-4 h-4 ml-1 rotate-90" />
          </div>
          <div className="bg-[#2B5CA6] text-white px-3 py-1.5 flex items-center gap-2 cursor-pointer hover:bg-blue-800 transition-colors">
            <div className="w-6 h-6 bg-gray-300 rounded-full overflow-hidden flex items-center justify-center">
              <User className="w-4 h-4 text-gray-600" />
            </div>
            <span className="text-sm font-medium">Rahul Sharma</span>
            <ChevronRight className="w-4 h-4 rotate-90" />
          </div>
        </div>
      </div>

      {/* Chevron Progress Bar */}
      <div className="bg-white px-6 py-4">
        <div className="flex w-full overflow-x-auto pb-2 scrollbar-hide">
          <div className="flex items-center w-max rounded-md overflow-hidden border border-gray-200 shadow-sm">
            {stages.map((stage, idx) => (
              <div
                key={idx}
                className={`relative flex items-center justify-center h-8 px-4 text-xs font-semibold tracking-wide whitespace-nowrap
                  ${getStageColor(stage.status)}
                  ${idx !== 0 ? '-ml-3 pl-6' : ''}
                `}
                style={{
                  clipPath: idx === 0 ? 'polygon(0% 0%, 90% 0%, 100% 50%, 90% 100%, 0% 100%)' :
                    idx === stages.length - 1 ? 'polygon(10% 0%, 100% 0%, 100% 100%, 10% 100%, 0% 50%)' :
                      'polygon(10% 0%, 90% 0%, 100% 50%, 90% 100%, 10% 100%, 0% 50%)',
                  zIndex: stages.length - idx
                }}
              >
                {stage.name}
              </div>
            ))}
            <div className="bg-gray-200 h-8 flex items-center justify-center px-2 -ml-3 pl-5 opacity-80" style={{ clipPath: 'polygon(20% 0%, 100% 0%, 100% 100%, 20% 100%, 0% 50%)' }}>
              <ChevronRight className="w-4 h-4 text-gray-500" />
            </div>
          </div>
        </div>

        {/* Top Cards Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">

          {/* Patient Info Card */}
          <div className="lg:col-span-2 border border-gray-200 rounded-lg shadow-sm">
            <div className="px-4 py-2 bg-white border-b border-gray-200">
              <h3 className="font-bold text-[#335380] text-sm uppercase tracking-wide flex items-center">
                <span className="w-1 h-4 bg-[#335380] mr-2 rounded-sm" /> Patient Info
              </h3>
            </div>
            <div className="bg-white p-4 space-y-3">
              <div className="grid grid-cols-3 text-sm py-2 border-b border-gray-100">
                <span className="text-gray-500 font-medium col-span-1">Category:</span>
                <span className="text-gray-900 font-bold col-span-2">Ayushman</span>
              </div>
              <div className="grid grid-cols-3 text-sm py-2 border-b border-gray-100">
                <span className="text-gray-500 font-medium col-span-1">Case Source:</span>
                <span className="text-gray-800 font-semibold col-span-2">Hospital Referral</span>
              </div>
              <div className="grid grid-cols-3 text-sm py-2">
                <span className="text-gray-500 font-medium col-span-1">Hospital:</span>
                <span className="text-gray-800 font-semibold col-span-2">City Hospital</span>
              </div>
            </div>
          </div>

          {/* Commercials Card */}
          <div className="border border-gray-200 rounded-lg shadow-md bg-[#F4F7FB] -mt-2 lg:mt-0 relative z-10 lg:transform lg:scale-105">
            <div className="px-4 py-2 border-b border-gray-200 bg-white rounded-t-lg">
              <h3 className="font-bold text-[#335380] text-sm uppercase tracking-wide flex items-center">
                <span className="w-1 h-4 bg-[#E59845] mr-2 rounded-sm" /> Commercials
              </h3>
            </div>
            <div className="p-4 space-y-2.5 text-sm">
              <div className="flex justify-between items-center py-2 border-b border-gray-200 bg-[#E8EEF5] px-2 rounded -mx-2">
                <span className="text-gray-700 font-medium">Package Amount:</span>
                <span className="text-gray-900 font-bold text-base">₹80,000</span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-gray-600">DOXEZ Cut:</span>
                <span className="text-gray-800 font-semibold">35%</span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-gray-600">Surgeon Payout:</span>
                <span className="text-gray-800 font-semibold">₹56,000</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-gray-200 pb-3">
                <span className="text-gray-600">Anaesthetist Payout:</span>
                <span className="text-gray-800 font-semibold">₹16,000</span>
              </div>
              <div className="flex justify-between items-center pt-1 pb-1">
                <span className="text-gray-700 font-medium">DOXEZ Margin:</span>
                <span className="text-[#335380] font-bold text-base">₹8,000</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6 pt-0 mt-6 lg:mt-0">

        {/* Doctor Availability & Pre-Lock Card */}
        <div className="bg-gray-50 rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="bg-white px-5 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-bold text-[#143E78] text-[15px] flex items-center">
              Doctor Availability & Pre-Lock
            </h3>
          </div>

          <div className="p-5">
            {/* Dropdowns */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase">Available Surgeons</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-3.5 w-3.5 text-gray-400" />
                  </div>
                  <select className="w-full pl-9 right-0 border border-gray-300 rounded shadow-sm text-sm py-2 bg-white text-gray-800 font-medium focus:outline-none focus:ring-1 focus:ring-blue-500 appearance-none">
                    <option>Dr. Sandeep Mehra</option>
                  </select>
                  <ChevronRight className="absolute right-3 top-3 w-4 h-4 rotate-90 text-gray-500" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase">Available Anaesthetists</label>
                <div className="relative">
                  <select className="w-full px-3 border border-gray-300 rounded shadow-sm text-sm py-2 bg-white text-gray-800 font-medium focus:outline-none focus:ring-1 focus:ring-blue-500 appearance-none">
                    <option>Dr. Anil Verma</option>
                  </select>
                  <ChevronRight className="absolute right-3 top-3 w-4 h-4 rotate-90 text-gray-500" />
                </div>
              </div>
            </div>

            {/* Common Time Slots */}
            <div className="mb-6">
              <h4 className="text-xs font-semibold text-gray-500 mb-3 tracking-wide">Common Time Slots</h4>

              <div className="space-y-3">
                {/* Slot 1 */}
                <div className="flex flex-wrap items-center gap-3 py-2 border-b border-gray-200 border-dashed pb-4">
                  <div className="w-5 h-5 rounded-full border-2 border-blue-600 flex items-center justify-center bg-blue-50">
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-600"></div>
                  </div>
                  <div className="bg-white px-3 py-1.5 rounded border border-gray-200 text-sm font-semibold text-gray-800 shadow-sm whitespace-nowrap">
                    Mar 24, 10:00 AM - 1:00 PM
                  </div>
                  <div className="bg-blue-50/50 px-3 py-1 rounded border border-blue-100 text-xs text-blue-700 font-medium flex items-center gap-1.5 ml-2 cursor-pointer hover:bg-blue-100">
                    <CheckCircle className="w-3.5 h-3.5" /> Mar 24, 3:00 PM - 6:00 PM
                  </div>
                </div>

                {/* Slot 2 */}
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full border-2 border-gray-300 bg-white"></div>
                  <div className="bg-white px-3 py-1.5 rounded border border-gray-200 text-sm font-medium text-gray-600 shadow-sm">
                    Mar 25, 11:00 AM - 2:00 PM
                  </div>
                </div>
              </div>
            </div>

            {/* Pre-Lock Details */}
            <div className="bg-[#EBF1F9] rounded border border-blue-100 p-4 mb-6">
              <div className="space-y-2">
                <div className="grid grid-cols-[160px_1fr] text-sm">
                  <span className="text-slate-600 font-medium">Pre-Locked Surgeon:</span>
                  <span className="font-bold text-slate-800">Dr. Sandeep Mehra</span>
                </div>
                <div className="grid grid-cols-[160px_1fr] text-sm">
                  <span className="text-slate-600 font-medium">Pre-Locked Anaesthetist:</span>
                  <span className="font-bold text-slate-800">Dr. Anil Verma</span>
                </div>
                <div className="grid grid-cols-[160px_1fr] text-sm mt-1 pt-2 border-t border-blue-200/50">
                  <span className="text-slate-600">Pre-Lock Time Window:</span>
                  <span className="font-bold text-[#143E78]">Mar 24, 10 AM - 1 PM</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <button className="bg-[#2B5CA6] hover:bg-blue-800 transition-colors text-white text-sm font-semibold py-2.5 px-6 rounded shadow-sm">
                Check Availability
              </button>
              <button className="bg-[#48914A] hover:bg-green-700 transition-colors text-white text-sm font-semibold py-2.5 px-6 rounded shadow-sm">
                Lock Doctors
              </button>
            </div>
          </div>
        </div>

        {/* Automation Map Card */}
        <div className="bg-[#EEF1F6] rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="bg-white px-5 py-3 border-b border-gray-200">
            <h3 className="font-bold text-[#143E78] text-[15px]">
              Automation Trigger Map (IF-THEN)
            </h3>
          </div>

          <div className="p-6 relative">
            {/* Flowchart Content */}
            <div className="grid grid-cols-[1fr_20px_1fr] gap-x-2 gap-y-5">

              {/* Row 1 */}
              <div className="bg-[#F8EBCD] border-l-4 border-[#DEB044] rounded shadow-sm overflow-hidden flex flex-col justify-center">
                <div className="px-3 py-1 text-xs text-yellow-900 border-b border-yellow-200/50">IF Clinical Decision = <span className="font-bold">Surgery Approved</span></div>
                <div className="bg-[#DEB044] text-white px-3 py-1.5 text-xs font-semibold">THEN Check Doctor Availability</div>
              </div>

              <div className="flex items-center justify-center text-gray-400">
                <ChevronRight />
              </div>

              <div className="bg-[#DFF1EB] border-l-4 border-[#257F60] rounded shadow-sm overflow-hidden flex flex-col justify-center">
                <div className="px-3 py-1 text-xs text-teal-900 border-b border-teal-200/50">IF Doctors Matched</div>
                <div className="bg-[#257F60] text-white px-3 py-1.5 text-xs font-semibold">THEN Pre-Lock Surgeons & Anashetist</div>
              </div>

              {/* Connecting Lines (Simulated visually via Layout) */}
              <div className="col-span-3 flex justify-end h-6 -my-3 pr-[45%]">
                <div className="w-[2px] h-full bg-gray-300 mr-2 relative">
                  <div className="absolute -bottom-1 -left-1 w-2.5 h-2.5 border-r-2 border-b-2 border-gray-400 rotate-45"></div>
                </div>
              </div>

              {/* Row 2 */}
              <div className="bg-[#E4ECF7] border-l-4 border-[#2B5CA6] rounded shadow-sm overflow-hidden flex flex-col justify-center">
                <div className="px-3 py-1 text-xs text-blue-900 border-b border-blue-200/50">IF Pre-Lock Expired</div>
                <div className="bg-[#2B5CA6] text-white px-3 py-1.5 text-xs font-semibold">THEN Auto Release Doctors</div>
              </div>

              <div className="flex items-center justify-center text-gray-400">
                <ChevronRight />
              </div>

              <div className="bg-[#FADBDC] border-l-4 border-[#D33E3B] rounded shadow-sm overflow-hidden flex flex-col justify-center">
                <div className="px-3 py-1 text-xs text-red-900 border-b border-red-200/50">IF OT Slot Out of Window</div>
                <div className="bg-[#D33E3B] text-white px-3 py-1.5 text-xs font-semibold">THEN Block Scheduling</div>
              </div>

              {/* Row 3 */}
              <div className="bg-[#FAEDDD] border-l-4 border-[#D86938] rounded shadow-sm overflow-hidden flex flex-col justify-center mt-3">
                <div className="px-3 py-1 text-xs text-orange-900 border-b border-orange-200/50">IF Payment Status ≠ <span className="font-bold">Paid</span></div>
                <div className="bg-[#D86938] text-white px-3 py-1.5 text-xs font-semibold">THEN Stop Progression</div>
              </div>
              <div className="col-span-2"></div>

            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SurgeryCaseDashboard;
